import { defineStore } from 'pinia'
import axiosInstance from '@/api/axios'
import { buildSteps } from '@/utils/qaTour'

/**
 * 테스트 관리(qa) 스토어 — 진행 중 회차에서 "내게 배정된 시나리오"를 현재 화면 기준으로 보관.
 * HelpPanel 의 테스트 탭 · 네비 배지 · 내 테스트 화면이 같은 데이터를 쓴다.
 */
export const useQaStore = defineStore('qa', {
  state: () => ({
    loaded: false,
    loading: false,
    path: '',
    run: null,          // 진행 중 회차 (내 배정이 있는)
    runs: [],
    assignments: [],
    here: [],           // 현재 화면 시나리오
    all: [],            // 내 전체 시나리오
    common: [],         // 공통 테스트 데이터
    pendingCount: 0,
    drawer: false,      // 전역 테스트 서랍 열림
    focusId: null,      // 이동 후 서랍에서 펼칠 시나리오
    hint: '',           // 이동 후 서랍 상단 안내 (상세 화면은 목록에서 열어야 할 때 등)
    pendingTourId: null, // 이동 후 서랍이 받으면 바로 따라하기 시작할 시나리오
    tour: { active: false, scenario: null, steps: [], index: 0 },
    error: null,
  }),
  getters: {
    hasRun: (s) => !!s.run,
    herePending: (s) => s.here.filter(x => !x.done).length,
  },
  actions: {
    async fetch(path, { force = false, runId = null } = {}) {
      const p = (path || window.location.pathname || '').split('?')[0]
      if (!force && this.loaded && this.path === p) return
      this.loading = true
      try {
        const params = { path: p }
        if (runId) params.run = runId
        const res = await axiosInstance.get('qa/my/', { params })
        const d = res.data || {}
        this.path = p
        this.run = d.run || null
        this.runs = d.runs || []
        this.assignments = d.assignments || []
        this.here = d.here || []
        this.all = d.all || []
        this.common = d.common || []
        this.pendingCount = d.pending_count || 0
        this.loaded = true
        this.error = null
      } catch (e) {
        this.error = e
        this.loaded = true
      } finally {
        this.loading = false
      }
    },
    /** 시나리오의 화면으로 이동하고 오른쪽 서랍을 그 항목이 펼쳐진 채로 연다 (2026-10-18 개선). */
    openScenario(s, router) {
      const raw = (s.route || '').split('?')[0]
      const common = this.common || []
      const styleNo = (common.find(c => (c.label || '').includes('고객사번호')) || {}).value || ''
      this.focusId = s.id
      this.hint = ''
      if (!raw) {
        this.hint = `${s.screen_path || '해당 화면'} 으로 직접 이동한 뒤 이 서랍에서 이어 가세요.`
        this.drawer = true
        return
      }
      let target = raw
      if (raw.includes(':')) {
        // /main-requests/:id → 목록(/main-requests)으로 보내고 안내. 목록 화면이 없는 상세는 상위 목록으로.
        const LIST_FOR = { '/work-instructions/:id': '/main-requests' }
        target = LIST_FOR[raw] || raw.split('/:')[0] || '/'
        const no = styleNo ? styleNo.split(' ')[0] : 'MKTEST-JP001'
        this.hint = LIST_FOR[raw]
          ? `메인오더 목록에서 테스트 건(${no})을 열고 생산지시서로 들어가면 이 항목이 이어집니다.`
          : `목록에서 테스트 건(${no})을 열면 이 항목이 이어집니다.`
      }
      this.drawer = true
      if (router && router.currentRoute.value.path !== target) router.push(target)
      else this.fetch(target, { force: true })
    },
    /** 따라하기 시작 — 서랍은 접고 화면 위에 ①②③ */
    startTour(s) {
      const steps = buildSteps(s)
      if (!steps.length) return
      this.tour = { active: true, scenario: s, steps, index: 0 }
      this.drawer = false
      this.pendingTourId = null
    },
    endTour() { this.tour = { active: false, scenario: null, steps: [], index: 0 } },
    /** 절차 끝 → 서랍을 그 항목으로 열어 확인할 것 체크 */
    finishTour() {
      const s = this.tour.scenario
      this.endTour()
      if (s) { this.focusId = s.id; this.drawer = true }
    },
    openDrawerFromTour() { if (this.tour.scenario) this.focusId = this.tour.scenario.id; this.drawer = true },
    /** 다른 화면의 시나리오를 따라하기 — 이동 후 서랍이 받으면 시작 */
    tourScenario(s, router) {
      const raw = (s.route || '').split('?')[0]
      const onScreen = raw && !raw.includes(':') && router && router.currentRoute.value.path === raw
      if (onScreen || !raw) { this.startTour(s); return }
      this.pendingTourId = s.id
      this.openScenario(s, router)
    },
    async refresh() {
      return this.fetch(this.path, { force: true, runId: this.run?.id })
    },
    reset() {
      this.$reset()
    },
  },
})
